<script lang="ts">
  import type { Api } from "../../lib/api";
  import { ApiError } from "../../lib/api";
  import Card from "../../lib/ui/Card.svelte";
  import Field from "../../lib/ui/Field.svelte";
  import Button from "../../lib/ui/Button.svelte";
  import Banner from "../../lib/ui/Banner.svelte";
  import { Select, SelectTrigger, SelectContent, SelectItem } from "@price-check/ui";

  let {
    api,
    onCreated,
    // When set, the form creates ONLY this kind of account and hides the role
    // picker — each page (Users = portal, Organizations = app) owns one role.
    fixedRole,
    title = "Add a user",
    subtitle = "Leave the password blank to email an invite instead.",
  }: {
    api: Api;
    onCreated: () => void;
    fixedRole?: "app" | "portal";
    title?: string;
    subtitle?: string;
  } = $props();

  const ROLE_OPTIONS: { value: "app" | "portal"; label: string }[] = [
    { value: "app", label: "App user" },
    { value: "portal", label: "Portal admin" },
  ];

  let email = $state("");
  // The picker default; when `fixedRole` is set the picker is hidden and submit
  // uses `fixedRole` directly (so we don't seed state from a prop).
  let role = $state<"portal" | "app">("app");
  const roleLabel = $derived(ROLE_OPTIONS.find((o) => o.value === role)?.label ?? "");
  let password = $state("");
  let busy = $state(false);
  let error = $state("");
  let notice = $state("");

  async function submit(e: Event) {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr) return;
    error = "";
    notice = "";
    busy = true;
    const pw = password.trim();
    try {
      await api.createUser(addr, fixedRole ?? role, pw || undefined);
      notice = pw ? `Created ${addr}.` : `Invite sent to ${addr}.`;
      email = "";
      password = "";
      onCreated();
    } catch (err) {
      error = err instanceof ApiError ? err.message : "Could not create the user.";
    } finally {
      busy = false;
    }
  }
</script>

<Card {title} {subtitle}>
  <form class="flex flex-col gap-3.5" onsubmit={submit}>
    <div
      class="grid {fixedRole
        ? 'grid-cols-[2fr_1.5fr_auto]'
        : 'grid-cols-[2fr_1fr_1.5fr_auto]'} gap-3 items-end max-[720px]:grid-cols-1"
    >
      <div class="flex flex-col gap-1.5 min-w-0">
        <Field
          id="nu-email"
          label="Email"
          type="email"
          placeholder="person@company.com"
          autocomplete="off"
          bind:value={email}
        />
      </div>
      {#if !fixedRole}
        <div class="flex flex-col gap-1.5 min-w-0">
          <span class="text-sm font-semibold text-ink">Role</span>
          <Select
            type="single"
            value={role}
            onValueChange={(v) => v && (role = v as "portal" | "app")}
          >
            <SelectTrigger class="w-full" aria-label="Role">{roleLabel}</SelectTrigger>
            <SelectContent>
              {#each ROLE_OPTIONS as o (o.value)}<SelectItem value={o.value} label={o.label} />{/each}
            </SelectContent>
          </Select>
        </div>
      {/if}
      <div class="flex flex-col gap-1.5 min-w-0">
        <Field
          id="nu-pw"
          label="Password (optional)"
          type="password"
          placeholder="auto-invite if blank"
          autocomplete="new-password"
          bind:value={password}
        />
      </div>
      <div class="flex flex-col gap-1.5 min-w-0 self-end [&_button]:max-[720px]:w-full">
        <Button type="submit" disabled={busy || !email.trim()}>
          {busy ? "Working…" : password.trim() ? "Create user" : "Send invite"}
        </Button>
      </div>
    </div>
    {#if error}<Banner tone="danger">{error}</Banner>{/if}
    {#if notice}<Banner tone="ok">{notice}</Banner>{/if}
  </form>
</Card>
